import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { MSI_V2_STAGED_IDENTIFIER_COLUMNS } from '../config/UploadConstant';
import {
    completeWatchlistM365Authorization,
    configureWatchlistM365Source,
    disconnectWatchlistM365,
    fetchWatchlistM365Status,
    inspectWatchlistM365Source,
    isSafeWatchlistM365AuthorizationUrl,
    isSupportedWatchlistM365SharingUrl,
    startWatchlistM365Authorization,
    validateWatchlistM365Source,
    WatchlistM365ApiError,
} from '../utils/watchlistM365Api';
import {
    rememberWatchlistM365BrowserSession,
    takeWatchlistM365Authorization,
} from '../utils/watchlistM365Callback';
import { WATCHLIST_M365_CONNECTION_CHANGED_EVENT } from '../utils/securityResolutionEvents';
import { downloadUploadTemplate } from '../utils/uploadTemplateDownload';

const EMPTY_CONNECTION = Object.freeze({
    connected: false,
    status: 'disconnected',
    generation: null,
    workbook_display_name: null,
    worksheet_name: null,
});

const SOURCE_ATTENTION_CODES = new Set([
    'sharing_url_invalid',
    'source_unavailable',
    'source_forbidden',
    'source_too_large',
    'workbook_invalid',
    'workbook_unsupported',
    'worksheet_reselection_required',
    'worksheet_invalid',
]);

const PERMISSION_COPY = (
    <>
        Alphamizer requests Microsoft delegated <strong>Files.Read</strong> permission for
        your work or school account. This lets Alphamizer read files that account can
        access. This feature reads only the workbook you link and never writes to Excel.
    </>
);

function defaultNavigateToMicrosoft(url) {
    window.location.assign(url);
}

function boundedDisplayLabel(value, limit) {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().replace(/\s+/g, ' ');
    return normalized ? normalized.slice(0, limit) : null;
}

function normalizeConnection(value) {
    const connection = value && typeof value === 'object' ? value : {};
    return {
        connected: connection.connected === true,
        status: typeof connection.status === 'string' ? connection.status.trim() : '',
        generation: Number.isInteger(connection.generation) && connection.generation > 0
            ? connection.generation
            : null,
        workbook_display_name: boundedDisplayLabel(connection.workbook_display_name, 160),
        worksheet_name: boundedDisplayLabel(connection.worksheet_name, 128),
    };
}

function safeConnectionLabel(connection) {
    const workbook = connection?.workbook_display_name || '';
    const worksheet = connection?.worksheet_name || '';
    if (!workbook || !worksheet) return '';
    return `${workbook} — ${worksheet}`;
}

function retainedWorkbookGeneration(connection) {
    return (
        connection?.connected === true
        && Number.isInteger(connection?.generation)
        && connection.generation > 0
        && connection?.workbook_display_name
    ) ? connection.generation : null;
}

function activeConnectionGeneration(connection) {
    return (
        retainedWorkbookGeneration(connection) != null
        && connection?.status === 'active'
        && connection?.worksheet_name
    ) ? connection.generation : null;
}

function connectionLabel(connection) {
    return safeConnectionLabel(connection)
        || connection?.workbook_display_name
        || 'No workbook selected';
}

function notifyWatchlistM365ConnectionChanged() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(WATCHLIST_M365_CONNECTION_CHANGED_EVENT));
    }
}

function stateChip(connection, busyAction, sourceMode, errorCode, capability) {
    if (capability && (!capability.enabled || !capability.configured)) return { label: 'Unavailable', color: 'default' };
    if (errorCode && SOURCE_ATTENTION_CODES.has(errorCode)) return { label: 'Source needs attention', color: 'warning' };
    if (errorCode) return { label: 'Action needed', color: 'warning' };
    if (busyAction === 'authorize') return { label: 'Authorization in progress', color: 'info' };
    if (busyAction === 'inspect') return { label: 'Validating link', color: 'info' };
    if (busyAction === 'configure') return { label: 'Saving worksheet', color: 'info' };
    if (busyAction === 'validate') return { label: 'Validating connection', color: 'info' };
    if (busyAction === 'disconnect') return { label: 'Disconnecting', color: 'info' };
    if (sourceMode === 'edit' && connection?.connected) return { label: 'Reconfiguring', color: 'info' };
    if (sourceMode === 'select' && retainedWorkbookGeneration(connection) != null) return { label: 'Worksheet selection', color: 'info' };
    if (activeConnectionGeneration(connection) != null) return { label: 'Active', color: 'success' };
    if (connection?.status === 'reconnect_required') return { label: 'Reconnect required', color: 'warning' };
    if (connection?.status === 'needs_worksheet' && retainedWorkbookGeneration(connection) != null) return { label: 'Worksheet needed', color: 'warning' };
    if (connection?.connected) return { label: 'Connected', color: 'info' };
    return { label: 'Not connected', color: 'default' };
}

function SetupStep({ number, title, summary, status, current, children }) {
    return (
        <Box
            component="li"
            aria-current={current ? 'step' : undefined}
            aria-label={`${number}. ${title}: ${status}`}
            sx={{
                listStyle: 'none',
                border: '1px solid',
                borderColor: current ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: { xs: 1.25, sm: 1.5 },
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start' }}>
                <Box
                    aria-hidden="true"
                    sx={{
                        width: 28,
                        height: 28,
                        flex: '0 0 28px',
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: current ? 'primary.main' : 'action.selected',
                        color: current ? 'primary.contrastText' : 'text.primary',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                    }}
                >
                    {number}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{title}</Typography>
                        <Typography variant="caption" color={current ? 'primary.main' : 'text.secondary'}>
                            {status}
                        </Typography>
                    </Box>
                    {!current && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, lineHeight: 1.45 }}>
                            {summary}
                        </Typography>
                    )}
                </Box>
            </Box>
            {current && (
                <Box sx={{ mt: 1.2, ml: { xs: 0, sm: 4.9 }, minWidth: 0 }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

SetupStep.propTypes = {
    number: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    current: PropTypes.bool.isRequired,
    children: PropTypes.node,
};

export default function LinkedExcelWatchlistCard({
    sessionKey,
    callbackResult = null,
    navigateToMicrosoft = defaultNavigateToMicrosoft,
    onGoToWatchlist,
    hideTitle = false,
}) {
    const [capability, setCapability] = useState(null);
    const [connection, setConnection] = useState(EMPTY_CONNECTION);
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(false);
    const [busyAction, setBusyAction] = useState(null);
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState(null);
    const [notice, setNotice] = useState('');
    const [noticeSeverity, setNoticeSeverity] = useState('success');
    const [sourceMode, setSourceMode] = useState('view');
    const [sharingUrl, setSharingUrl] = useState('');
    const [worksheetNames, setWorksheetNames] = useState([]);
    const [selectedWorksheet, setSelectedWorksheet] = useState('');
    const [disconnectOpen, setDisconnectOpen] = useState(false);
    const [goingToWatchlist, setGoingToWatchlist] = useState(false);
    const [focusTarget, setFocusTarget] = useState(null);
    const [showCompletionStep, setShowCompletionStep] = useState(false);

    const mountedRef = useRef(false);
    const epochRef = useRef(0);
    // 'connected' or 'error' once Microsoft has sent the user back here.
    const callbackOutcomeRef = useRef(null);
    const busyRef = useRef(false);
    const controllersRef = useRef(new Set());
    const sourceActionRef = useRef(null);
    const goToWatchlistRef = useRef(false);
    const errorRef = useRef(null);
    const connectButtonRef = useRef(null);
    const sharingInputRef = useRef(null);
    const worksheetSelectRef = useRef(null);
    const goButtonRef = useRef(null);

    useEffect(() => {
        if (!focusTarget || typeof window === 'undefined') return undefined;
        const focusRefs = {
            connect: connectButtonRef,
            error: errorRef,
            link: sharingInputRef,
            worksheet: worksheetSelectRef,
            watchlist: goButtonRef,
        };
        const frame = window.requestAnimationFrame?.(() => {
            focusRefs[focusTarget]?.current?.focus?.();
            setFocusTarget(null);
        });
        return () => {
            if (frame !== undefined) window.cancelAnimationFrame?.(frame);
        };
    }, [focusTarget]);

    const beginRequest = useCallback(() => {
        const controller = new AbortController();
        controllersRef.current.add(controller);
        return controller;
    }, []);

    const finishRequest = useCallback((controller) => {
        controllersRef.current.delete(controller);
    }, []);

    const isCurrent = useCallback((epoch) => mountedRef.current && epochRef.current === epoch, []);

    const applyStatus = useCallback((response) => {
        const normalizedConnection = normalizeConnection(response?.connection);
        setCapability({
            enabled: response?.enabled === true,
            configured: response?.configured === true,
        });
        setConnection(normalizedConnection);
        return normalizedConnection;
    }, []);

    const loadStatus = useCallback(async (epoch, { quiet = false } = {}) => {
        const controller = beginRequest();
        if (!quiet) setLoading(true);
        try {
            const response = await fetchWatchlistM365Status({ signal: controller.signal });
            if (!isCurrent(epoch)) return null;
            const confirmedConnection = applyStatus(response);
            notifyWatchlistM365ConnectionChanged();
            const callbackOutcome = callbackOutcomeRef.current;
            if (callbackOutcome === 'connected') {
                if (confirmedConnection.connected === true) {
                    setNotice('Microsoft connection confirmed. Choose or review the linked workbook below.');
                    setNoticeSeverity('success');
                    setFocusTarget(
                        activeConnectionGeneration(confirmedConnection) != null
                            ? 'watchlist'
                            : confirmedConnection.status === 'needs_worksheet'
                                && retainedWorkbookGeneration(confirmedConnection) != null
                                ? 'worksheet'
                                : 'link',
                    );
                } else {
                    setNotice('Microsoft returned to Alphamizer, but the connection could not be confirmed. Start Connect Microsoft again.');
                    setNoticeSeverity('warning');
                    setFocusTarget('connect');
                }
            } else if (callbackOutcome === 'error') {
                setNoticeSeverity('warning');
                if (activeConnectionGeneration(confirmedConnection) != null) {
                    setNotice('Microsoft authorization did not complete, but your existing linked workbook is still active.');
                    setFocusTarget('watchlist');
                } else if (
                    confirmedConnection.status === 'needs_worksheet'
                    && retainedWorkbookGeneration(confirmedConnection) != null
                ) {
                    setNotice('Microsoft authorization did not complete. Continue worksheet setup for the retained workbook.');
                    setFocusTarget('worksheet');
                } else if (
                    confirmedConnection.connected === true
                    && confirmedConnection.status !== 'reconnect_required'
                ) {
                    setNotice('Microsoft authorization did not complete. Continue linking the workbook below.');
                    setFocusTarget('link');
                } else {
                    setNotice(confirmedConnection.status === 'reconnect_required'
                        ? 'Microsoft authorization did not complete. Start Reconnect Microsoft again.'
                        : 'Microsoft authorization did not complete. Start Connect Microsoft again.');
                    setFocusTarget('connect');
                }
            }
            return response;
        } catch (requestError) {
            if (!isCurrent(epoch) || requestError?.name === 'AbortError') return null;
            const safeError = requestError instanceof WatchlistM365ApiError
                ? requestError
                : new WatchlistM365ApiError('connection_unavailable');
            setError(safeError.message);
            setErrorCode(safeError.code);
            setFocusTarget('error');
            return null;
        } finally {
            finishRequest(controller);
            if (isCurrent(epoch) && !quiet) setLoading(false);
        }
    }, [applyStatus, beginRequest, finishRequest, isCurrent]);

    const finishAuthorization = useCallback(async (epoch) => {
        const authorization = takeWatchlistM365Authorization();
        if (authorization) {
            const controller = beginRequest();
            try {
                await completeWatchlistM365Authorization(authorization, { signal: controller.signal });
                callbackOutcomeRef.current = 'connected';
            } catch (requestError) {
                if (!isCurrent(epoch) || requestError?.name === 'AbortError') return;
                const safeError = requestError instanceof WatchlistM365ApiError
                    ? requestError
                    : new WatchlistM365ApiError('connection_unavailable');
                setError(safeError.message);
                setErrorCode(safeError.code);
            } finally {
                finishRequest(controller);
            }
        }
        if (!isCurrent(epoch)) return;
        setCompleting(false);
        await loadStatus(epoch);
    }, [beginRequest, finishRequest, isCurrent, loadStatus]);

    useEffect(() => {
        const controllers = controllersRef.current;
        mountedRef.current = true;
        epochRef.current += 1;
        const epoch = epochRef.current;
        busyRef.current = false;
        controllers.forEach((controller) => controller.abort());
        controllers.clear();
        setCapability(null);
        setConnection(EMPTY_CONNECTION);
        setLoading(true);
        setBusyAction(null);
        setError('');
        setErrorCode(null);
        setSourceMode('view');
        setSharingUrl('');
        setWorksheetNames([]);
        setSelectedWorksheet('');
        setDisconnectOpen(false);
        setGoingToWatchlist(false);
        setFocusTarget(null);
        setShowCompletionStep(false);
        goToWatchlistRef.current = false;
        // A return from Microsoft counts as failed until the backend confirms it.
        callbackOutcomeRef.current = callbackResult ? 'error' : null;
        setCompleting(callbackResult === 'authorized');
        if (callbackResult === 'authorized') {
            setNotice('Microsoft returned to Alphamizer. Finishing your connection…');
            setNoticeSeverity('info');
            void finishAuthorization(epoch);
        } else {
            if (callbackResult === 'error') {
                setNotice('Microsoft authorization did not complete. Start Connect Microsoft again.');
                setNoticeSeverity('warning');
            } else {
                setNotice('');
                setNoticeSeverity('success');
            }
            void loadStatus(epoch);
        }

        return () => {
            mountedRef.current = false;
            epochRef.current += 1;
            busyRef.current = false;
            goToWatchlistRef.current = false;
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
        };
    }, [callbackResult, finishAuthorization, loadStatus, sessionKey]);

    const runAction = useCallback(async (action, operation) => {
        if (busyRef.current) return null;
        busyRef.current = true;
        const epoch = epochRef.current;
        const controller = beginRequest();
        setBusyAction(action);
        setError('');
        setErrorCode(null);
        setNotice('');
        setNoticeSeverity('success');
        try {
            return await operation(controller.signal, epoch);
        } catch (requestError) {
            if (!isCurrent(epoch) || requestError?.name === 'AbortError') return null;
            const safeError = requestError instanceof WatchlistM365ApiError
                ? requestError
                : new WatchlistM365ApiError('connection_unavailable');
            setError(safeError.message);
            setErrorCode(safeError.code);
            setFocusTarget('error');
            if (SOURCE_ATTENTION_CODES.has(safeError.code)) {
                setSourceMode('edit');
                setWorksheetNames([]);
                setSelectedWorksheet('');
            }
            if (
                safeError.code === 'reconnect_required'
                || safeError.code === 'account_mismatch'
                || safeError.code === 'permission_missing'
            ) {
                setConnection((current) => ({ ...current, status: 'reconnect_required' }));
            }
            return null;
        } finally {
            finishRequest(controller);
            if (isCurrent(epoch)) {
                busyRef.current = false;
                setBusyAction(null);
            }
        }
    }, [beginRequest, finishRequest, isCurrent]);

    const handleConnect = useCallback(() => {
        void runAction('authorize', async (signal, epoch) => {
            const response = await startWatchlistM365Authorization({ signal });
            if (!isCurrent(epoch)) return;
            if (
                !isSafeWatchlistM365AuthorizationUrl(response.authorization_url)
                || !rememberWatchlistM365BrowserSession(response.browser_session)
            ) {
                throw new WatchlistM365ApiError('connection_unavailable');
            }
            navigateToMicrosoft(response.authorization_url);
        });
    }, [isCurrent, navigateToMicrosoft, runAction]);

    const handleInspect = useCallback(() => {
        const submittedUrl = sharingUrl;
        setSharingUrl('');
        setNotice('');
        setNoticeSeverity('success');
        if (submittedUrl !== submittedUrl.trim() || !isSupportedWatchlistM365SharingUrl(submittedUrl)) {
            setError(new WatchlistM365ApiError('sharing_url_invalid').message);
            setErrorCode('sharing_url_invalid');
            setFocusTarget('error');
            return;
        }
        void runAction('inspect', async (signal, epoch) => {
            const response = await inspectWatchlistM365Source(submittedUrl, { signal });
            if (!isCurrent(epoch)) return;
            setConnection(normalizeConnection(response.connection));
            notifyWatchlistM365ConnectionChanged();
            setWorksheetNames(Array.isArray(response.worksheet_names) ? response.worksheet_names : []);
            setSelectedWorksheet('');
            setSourceMode('select');
            setNotice('Workbook validated. Choose the worksheet that contains your Watchlist.');
            setNoticeSeverity('success');
            setFocusTarget('worksheet');
        });
    }, [isCurrent, runAction, sharingUrl]);

    const handleContinueWorksheetSetup = useCallback(() => {
        const generation = retainedWorkbookGeneration(connection);
        if (generation == null) return;
        void runAction('validate', async (signal, epoch) => {
            const response = await validateWatchlistM365Source(generation, { signal });
            if (!isCurrent(epoch)) return;
            setConnection(normalizeConnection(response.connection || connection));
            notifyWatchlistM365ConnectionChanged();
            setWorksheetNames(Array.isArray(response.worksheet_names) ? response.worksheet_names : []);
            setSelectedWorksheet('');
            setSourceMode('select');
            setNotice(connection?.status === 'active'
                ? 'Choose a worksheet. The current selection stays active until you confirm another one.'
                : 'Choose a worksheet to finish the connection.');
            setNoticeSeverity('success');
            setFocusTarget('worksheet');
        });
    }, [connection, isCurrent, runAction]);

    const handleConfigure = useCallback(() => {
        const generation = retainedWorkbookGeneration(connection);
        if (!selectedWorksheet || generation == null) return;
        void runAction('configure', async (signal, epoch) => {
            const response = await configureWatchlistM365Source(
                selectedWorksheet,
                generation,
                { signal },
            );
            if (!isCurrent(epoch)) return;
            const nextConnection = normalizeConnection(response.connection);
            const configuredActive = activeConnectionGeneration(nextConnection) != null;
            setConnection(nextConnection);
            notifyWatchlistM365ConnectionChanged();
            setWorksheetNames([]);
            setSelectedWorksheet('');
            setSourceMode('view');
            setShowCompletionStep(configuredActive);
            setNotice(configuredActive
                ? 'Linked Excel Watchlist is active. No Watchlist changes were imported.'
                : 'The worksheet selection was saved, but the active linked source could not be confirmed. Review the workbook connection below.');
            setNoticeSeverity(configuredActive ? 'success' : 'warning');
            setFocusTarget(configuredActive ? 'watchlist' : 'link');
        });
    }, [connection, isCurrent, runAction, selectedWorksheet]);

    const handleValidate = useCallback(() => {
        const generation = Number(connection?.generation);
        if (!Number.isInteger(generation) || generation < 1) return;
        void runAction('validate', async (signal, epoch) => {
            const response = await validateWatchlistM365Source(generation, { signal });
            if (!isCurrent(epoch)) return;
            const nextConnection = normalizeConnection(response.connection || connection);
            const validatedActive = activeConnectionGeneration(nextConnection) != null;
            setConnection(nextConnection);
            notifyWatchlistM365ConnectionChanged();
            setNotice(validatedActive
                ? 'Microsoft connection, workbook, and worksheet are available.'
                : 'The active linked source could not be confirmed. Review the workbook connection below.');
            setNoticeSeverity(validatedActive ? 'success' : 'warning');
            if (!validatedActive) setFocusTarget('link');
        });
    }, [connection, isCurrent, runAction]);

    const handleDisconnect = useCallback(() => {
        const generation = Number(connection?.generation);
        if (!Number.isInteger(generation) || generation < 1) return;
        setDisconnectOpen(false);
        void runAction('disconnect', async (signal, epoch) => {
            const response = await disconnectWatchlistM365(generation, { signal });
            if (!isCurrent(epoch)) return;
            setConnection(normalizeConnection(response.connection));
            notifyWatchlistM365ConnectionChanged();
            setSourceMode('view');
            setWorksheetNames([]);
            setSelectedWorksheet('');
            setNotice(
                'Linked Excel Watchlist was disconnected. Microsoft tenant consent may remain until you or an administrator revoke it in Microsoft.',
            );
            setNoticeSeverity('success');
        });
    }, [connection?.generation, isCurrent, runAction]);

    const resetSourceEditState = useCallback(() => {
        setSharingUrl('');
        setWorksheetNames([]);
        setSelectedWorksheet('');
        setError('');
        setErrorCode(null);
        setNotice('');
        setNoticeSeverity('success');
    }, []);

    const handleChooseWorkbook = useCallback(() => {
        resetSourceEditState();
        setSourceMode('edit');
        setFocusTarget('link');
    }, [resetSourceEditState]);

    const handleCancelSourceEdit = useCallback(() => {
        resetSourceEditState();
        setSourceMode('view');
        window.requestAnimationFrame?.(() => sourceActionRef.current?.focus());
    }, [resetSourceEditState]);

    const handleGoToWatchlist = useCallback(async () => {
        if (goToWatchlistRef.current || typeof onGoToWatchlist !== 'function') return;
        goToWatchlistRef.current = true;
        const epoch = epochRef.current;
        setGoingToWatchlist(true);
        setError('');
        setErrorCode(null);
        try {
            const navigated = await onGoToWatchlist();
            if (navigated === false && isCurrent(epoch)) {
                setError('Finish or correct the pending Settings changes before opening the Watchlist.');
                setErrorCode('settings_save_required');
                setFocusTarget('error');
            }
        } catch {
            if (isCurrent(epoch)) {
                setError('The Watchlist could not be opened. Your linked workbook is still configured.');
                setErrorCode('navigation_unavailable');
                setFocusTarget('error');
            }
        } finally {
            if (isCurrent(epoch)) {
                goToWatchlistRef.current = false;
                setGoingToWatchlist(false);
            }
        }
    }, [isCurrent, onGoToWatchlist]);

    const chip = stateChip(connection, busyAction, sourceMode, errorCode, capability);
    const unavailable = capability && (!capability.enabled || !capability.configured);
    const statusUnavailable = capability === null;
    const connected = connection?.connected === true;
    const active = activeConnectionGeneration(connection) != null;
    const retainedWorkbookValid = retainedWorkbookGeneration(connection) != null;
    const needsWorksheet = connection?.status === 'needs_worksheet' && retainedWorkbookValid;
    const reconnectRequired = connection?.status === 'reconnect_required';
    const controlsBusy = Boolean(busyAction) || goingToWatchlist;
    const setupStep = (!connected || reconnectRequired)
        ? 1
        : (sourceMode === 'edit' || connection?.status === 'connected_unconfigured')
            ? 2
            : ((sourceMode === 'select' && retainedWorkbookValid) || needsWorksheet)
                ? 3
                : active ? 4 : 2;
    const showSetupJourney = !active || sourceMode !== 'view' || reconnectRequired || showCompletionStep;

    const stepStatus = (number) => {
        if (number < setupStep) return 'Complete';
        if (number === setupStep) return 'Current step';
        return 'Next';
    };
    const actionRowSx = {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        flexWrap: 'wrap',
        gap: 0.8,
        '& > button': { width: { xs: '100%', sm: 'auto' } },
    };

    if (loading) {
        return (
            <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 44 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">
                    {completing ? 'Finishing your Microsoft connection…' : 'Loading Linked Excel Watchlist…'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: 1.4 }} data-testid="linked-excel-watchlist-card">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                {!hideTitle && (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Linked Excel Watchlist
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                            Link one organizational OneDrive or SharePoint .xlsx workbook. Updates are retrieved only when you explicitly sync from the Watchlist.
                        </Typography>
                    </Box>
                )}
                <Chip size="small" label={chip.label} color={chip.color} aria-label={`Connection status: ${chip.label}`} />
            </Box>

            {notice && <Alert severity={noticeSeverity} role="status">{notice}</Alert>}
            {error && (
                <Alert
                    ref={errorRef}
                    tabIndex={-1}
                    severity="error"
                    id="linked-excel-error"
                    role="alert"
                    aria-label="Linked Excel Watchlist error"
                >
                    {error}
                </Alert>
            )}

            {unavailable && (
                <Alert severity="info">
                    Linked Excel Watchlist is not available in this environment. Existing manual Watchlist upload remains available.
                </Alert>
            )}

            {statusUnavailable && (
                <Box sx={{ display: 'grid', gap: 0.8, justifyItems: 'start' }}>
                    <Alert severity="warning">
                        Connection status could not be confirmed. No connection action is available until Settings reloads successfully.
                    </Alert>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setError('');
                            setErrorCode(null);
                            void loadStatus(epochRef.current);
                        }}
                    >
                        Retry status
                    </Button>
                </Box>
            )}

            {!unavailable && !statusUnavailable && showSetupJourney && (
                <Box
                    component="ol"
                    aria-label="Linked Excel Watchlist setup steps"
                    sx={{ m: 0, p: 0, display: 'grid', gap: 1 }}
                >
                    <SetupStep
                        number={1}
                        title="Connect Microsoft"
                        summary="Connect an organizational work or school account with delegated Files.Read access."
                        status={stepStatus(1)}
                        current={setupStep === 1}
                    >
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            {reconnectRequired && (
                                <Alert severity="warning">
                                    Microsoft authorization is no longer available. Reconnect with the same organizational account.
                                </Alert>
                            )}
                            <Typography id="linked-excel-permission-note" variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                {PERMISSION_COPY} Administrator consent is not always required, but your organization’s tenant policy may require an administrator to approve it.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Connecting only establishes access. It does not import, stage, or apply Watchlist changes.
                            </Typography>
                            <Box sx={actionRowSx}>
                                <Button
                                    ref={connectButtonRef}
                                    variant="contained"
                                    onClick={handleConnect}
                                    disabled={controlsBusy}
                                    aria-describedby="linked-excel-permission-note"
                                >
                                    {busyAction === 'authorize'
                                        ? 'Opening Microsoft…'
                                        : reconnectRequired ? 'Reconnect Microsoft' : 'Connect Microsoft'}
                                </Button>
                                {connected && (
                                    <Button color="error" variant="outlined" onClick={() => setDisconnectOpen(true)} disabled={controlsBusy}>
                                        Disconnect
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </SetupStep>

                    <SetupStep
                        number={2}
                        title="Prepare and link workbook"
                        summary="Prepare one .xlsx Watchlist, then paste its Microsoft Share → Copy link."
                        status={stepStatus(2)}
                        current={setupStep === 2}
                    >
                        <Box sx={{ display: 'grid', gap: 1.15 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                Save one <strong>.xlsx</strong> workbook in your organization’s OneDrive for Business or SharePoint. Put headings in row 1 and one security on each following row.
                            </Typography>
                            <Box component="ul" sx={{ my: 0, pl: 2.4, color: 'text.secondary' }}>
                                <Typography component="li" variant="caption" sx={{ mb: 0.4 }}>
                                    Include a supported identifier column: {MSI_V2_STAGED_IDENTIFIER_COLUMNS.join(', ')}. If there are several, the most reliable is used for matching (SEDOL first, then FIGI, ISIN, CUSIP and vendor IDs, ticker last) and the others are ignored.
                                </Typography>
                                <Typography component="li" variant="caption" sx={{ mb: 0.4 }}>
                                    Country and MIC are optional identity hints. Other ordinary columns become optional Watchlist attributes.
                                </Typography>
                                <Typography component="li" variant="caption">
                                    Use beta or beta_&lt;source&gt; for optional user-supplied beta columns.
                                </Typography>
                            </Box>
                            <Box sx={actionRowSx}>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => downloadUploadTemplate('watchlist', 'excel')}
                                    disabled={controlsBusy}
                                >
                                    Download example Excel watchlist
                                </Button>
                            </Box>
                            <Alert severity="info">
                                In Microsoft 365, open the workbook and choose <strong>Share → Copy link</strong>. Paste that link below—do not copy the address from your browser’s address bar.
                            </Alert>
                            <Box
                                component="form"
                                noValidate
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    handleInspect();
                                }}
                                sx={{ display: 'grid', gap: 0.8 }}
                            >
                                <TextField
                                    inputRef={sharingInputRef}
                                    label="Microsoft Excel Copy link"
                                    type="password"
                                    value={sharingUrl}
                                    onChange={(event) => setSharingUrl(event.target.value)}
                                    autoComplete="off"
                                    inputProps={{
                                        'aria-describedby': error ? 'linked-excel-error linked-excel-link-help' : 'linked-excel-link-help',
                                        spellCheck: 'false',
                                    }}
                                    error={errorCode === 'sharing_url_invalid'}
                                    disabled={controlsBusy}
                                    fullWidth
                                />
                                <FormHelperText id="linked-excel-link-help">
                                    Only an organizational OneDrive or SharePoint Excel Copy link is supported. The value is cleared after submission and is never saved in this browser.
                                </FormHelperText>
                                <Box sx={actionRowSx}>
                                    <Button type="submit" variant="contained" disabled={controlsBusy || !sharingUrl.trim()}>
                                        {busyAction === 'inspect' ? 'Validating link…' : 'Validate link'}
                                    </Button>
                                    {(active || needsWorksheet) && (
                                        <Button type="button" onClick={handleCancelSourceEdit} disabled={controlsBusy}>
                                            Cancel
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </SetupStep>

                    <SetupStep
                        number={3}
                        title="Choose worksheet"
                        summary="Explicitly choose the one worksheet Alphamizer should read."
                        status={stepStatus(3)}
                        current={setupStep === 3}
                    >
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Alphamizer reads exactly the worksheet you choose and will never silently switch to another sheet.
                            </Typography>
                            {sourceMode === 'select' ? (
                                <>
                                    <FormControl fullWidth error={errorCode === 'worksheet_invalid'} disabled={controlsBusy}>
                                        <InputLabel id="linked-excel-worksheet-label">Worksheet</InputLabel>
                                        <Select
                                            inputRef={worksheetSelectRef}
                                            labelId="linked-excel-worksheet-label"
                                            label="Worksheet"
                                            value={selectedWorksheet}
                                            onChange={(event) => setSelectedWorksheet(event.target.value)}
                                            aria-describedby="linked-excel-worksheet-help"
                                        >
                                            {worksheetNames.map((worksheetName) => (
                                                <MenuItem key={worksheetName} value={worksheetName}>{worksheetName}</MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText id="linked-excel-worksheet-help">
                                            This explicit selection is retained until you change or disconnect it.
                                        </FormHelperText>
                                    </FormControl>
                                    {worksheetNames.length === 0 && (
                                        <Alert severity="warning">No selectable worksheets were returned. Choose the workbook again.</Alert>
                                    )}
                                    <Box sx={actionRowSx}>
                                        <Button variant="contained" onClick={handleConfigure} disabled={controlsBusy || !selectedWorksheet}>
                                            {busyAction === 'configure' ? 'Saving worksheet…' : 'Use this worksheet'}
                                        </Button>
                                        <Button onClick={handleChooseWorkbook} disabled={controlsBusy}>
                                            Choose another workbook
                                        </Button>
                                        {(active || needsWorksheet) && (
                                            <Button onClick={handleCancelSourceEdit} disabled={controlsBusy}>
                                                Cancel
                                            </Button>
                                        )}
                                    </Box>
                                </>
                            ) : (
                                <Box sx={actionRowSx}>
                                    <Button ref={worksheetSelectRef} variant="contained" onClick={handleContinueWorksheetSetup} disabled={controlsBusy}>
                                        {busyAction === 'validate' ? 'Loading worksheets…' : 'Choose worksheet'}
                                    </Button>
                                    <Button ref={sourceActionRef} onClick={handleChooseWorkbook} disabled={controlsBusy}>
                                        Choose another workbook
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </SetupStep>

                    <SetupStep
                        number={4}
                        title="Sync, Review, Apply"
                        summary="Open Watchlist, manually sync once, review the diff, then choose Apply."
                        status={stepStatus(4)}
                        current={setupStep === 4}
                    >
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Setup is complete. In Watchlist, open Watchlist History and choose Sync from Excel. If meaningful data changed, review the existing diff and choose Apply. Nothing is imported or applied automatically.
                            </Typography>
                            <Box sx={actionRowSx}>
                                <Button ref={goButtonRef} variant="contained" onClick={handleGoToWatchlist} disabled={controlsBusy || !onGoToWatchlist}>
                                    {goingToWatchlist ? 'Opening Watchlist…' : 'Go to Watchlist'}
                                </Button>
                            </Box>
                        </Box>
                    </SetupStep>
                </Box>
            )}

            {!unavailable && !statusUnavailable && active && !showSetupJourney && (
                <Box sx={{ display: 'grid', gap: 1.1 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Linked source</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                            {connectionLabel(connection)}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                        Setup complete. Go to Watchlist, open Watchlist History and choose Sync from Excel, review any meaningful changes, then choose Apply. Nothing is imported or applied automatically.
                    </Typography>
                    <Box sx={actionRowSx}>
                        <Button ref={goButtonRef} variant="contained" onClick={handleGoToWatchlist} disabled={controlsBusy || !onGoToWatchlist}>
                            {goingToWatchlist ? 'Opening Watchlist…' : 'Go to Watchlist'}
                        </Button>
                        <Button variant="outlined" onClick={handleValidate} disabled={controlsBusy}>
                            {busyAction === 'validate' ? 'Validating…' : 'Validate connection'}
                        </Button>
                        <Button variant="outlined" onClick={handleContinueWorksheetSetup} disabled={controlsBusy}>
                            Change worksheet
                        </Button>
                        <Button
                            ref={sourceActionRef}
                            variant="outlined"
                            onClick={handleChooseWorkbook}
                            disabled={controlsBusy}
                        >
                            Change workbook
                        </Button>
                        <Button color="error" variant="outlined" onClick={() => setDisconnectOpen(true)} disabled={controlsBusy}>
                            Disconnect
                        </Button>
                    </Box>
                </Box>
            )}

            <Dialog
                open={disconnectOpen}
                onClose={() => !controlsBusy && setDisconnectOpen(false)}
                aria-labelledby="linked-excel-disconnect-title"
                aria-describedby="linked-excel-disconnect-description"
            >
                <DialogTitle id="linked-excel-disconnect-title">Disconnect Linked Excel Watchlist?</DialogTitle>
                <DialogContent>
                    <Typography id="linked-excel-disconnect-description" variant="body2">
                        This removes Alphamizer’s stored Microsoft connection and workbook binding. It does not revoke Microsoft tenant consent; you or an administrator must revoke that separately in Microsoft if desired.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisconnectOpen(false)} disabled={controlsBusy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDisconnect} disabled={controlsBusy}>
                        Disconnect
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

LinkedExcelWatchlistCard.propTypes = {
    sessionKey: PropTypes.string,
    callbackResult: PropTypes.oneOf(['authorized', 'error']),
    navigateToMicrosoft: PropTypes.func,
    onGoToWatchlist: PropTypes.func,
    hideTitle: PropTypes.bool,
};
